import axios, { AxiosInstance } from "axios";
import { RpiApiContextProps, useRpiApiContext } from "./provider";

const restClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

const publicRestClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

export function getPublicRestClient() {
  if (!publicRestClient.defaults.baseURL) {
    throw new Error("Public rest client endpoint not set! Call configureRestClients before accessing.");
  }
  return publicRestClient;
}

export function getRestClient() {
  if (!restClient.defaults.baseURL) {
    throw new Error("Rest client endpoint not set! Call configureRestClients before accessing.");
  }
  return restClient;
}

export function configureRestClients(publicPrintApiEndpointUrl: string, clientApiUrl: string) {
  restClient.defaults.baseURL = publicPrintApiEndpointUrl;
  publicRestClient.defaults.baseURL = clientApiUrl;
}

// GraphQL
export async function rpiGraphqlRequest<TData, TVariables>(
  graphqlClient: AxiosInstance,
  query: string,
  variables?: TVariables,
  headers?: Record<string, string>
): Promise<TData> {
  return graphqlClient
    .post(
      "",
      { query, variables },
      {
        headers,
      }
    )
    .then((res) => {
      if (res.data.errors) {
        const errors: CustomGraphQLError[] = res.data.errors.map(
          (error: GraphQLError) => new CustomGraphQLError(error)
        );

        throw errors[0];
      }

      return res.data?.data;
    });
}

export function useRawGraphqlRequest() {
  const rpiApiContext = useRpiApiContext();

  const useGraphqlRequest = async <TData, TVariables>(query: string, variables?: TVariables): Promise<TData> => {
    return rpiGraphqlRequest(rpiApiContext.graphqlClient, query, variables, {
      ...(await getHeaders(rpiApiContext.headers)),
    });
  };

  return useGraphqlRequest;
}

export const useRpiGraphqlRequest = <TData, TVariables>(
  query: string,
  options?: Record<string, string>
): ((variables?: TVariables) => Promise<TData>) => {
  const rpiApiContext = useRpiApiContext();

  return async (variables) => {
    return rpiGraphqlRequest(rpiApiContext.graphqlClient, query, variables, {
      ...(await getHeaders(rpiApiContext.headers)),
      ...options,
    });
  };
};

export const getHeaders = async (headers: RpiApiContextProps["headers"]) => {
  if (typeof headers === "function") {
    return await Promise.resolve(headers());
  } else {
    return headers;
  }
};

interface GraphQLError {
  message: string;
  locations: Array<{
    line: number;
    column: number;
  }>;
  path: string[];
  extensions: {
    code: number;
    classification: string;
  };
}

export const isGraphQLError = (error: unknown): error is CustomGraphQLError => {
  return error instanceof CustomGraphQLError;
};

export class CustomGraphQLError extends Error {
  public data!: GraphQLError;

  constructor(data: GraphQLError) {
    super(data.message);

    this.name = "GraphQLError";
    this.data = data;
  }
}
